import { EXPERIMENTS_VALUES } from "@cart-client/constants/index";
import { RENDERING_DETAILS_KEYS } from "@cart-client/constants/Segmented";
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import { ReviewedCartApiResponse, RenderingDetails, CartCouponPositionXP } from "@swiggy-private/cart-types";
import { VALID_RENDERING_DETAILS_TYPE } from "@swiggy-private/cart-ui";
import { isEmpty } from "@swiggy-private/checkout-core";

export const reorderItemsForCouponXP = (
    renderingDetailsFromServer: RenderingDetails[],
    couponXP: CartCouponPositionXP | undefined,
): RenderingDetails[] => {
    if (couponXP !== EXPERIMENTS_VALUES.CART_APPLY_COUPON_POSITION.FIRST) {
        return renderingDetailsFromServer;
    }

    const couponsTypeIds: string[] = [
        VALID_RENDERING_DETAILS_TYPE.COUPON_BUTTON,
        VALID_RENDERING_DETAILS_TYPE.COUPON_REMOVE_BUTTON,
    ];

    const couponList: RenderingDetails[] = [];
    const otherItemsList: RenderingDetails[] = [];

    for (let index = 0; index < renderingDetailsFromServer.length; index++) {
        const item = renderingDetailsFromServer[index];

        if (couponsTypeIds.includes(item.type.toUpperCase())) {
            couponList.push(item);

            // Check if next item is separator or not, if yes move it as well
            if (
                renderingDetailsFromServer[index + 1]?.type.toUpperCase() ===
                VALID_RENDERING_DETAILS_TYPE.TRANSPARENT_SEPARATOR
            ) {
                index += 1;
                couponList.push(renderingDetailsFromServer[index]);
            }
        } else {
            otherItemsList.push(item);
        }
    }

    return [...couponList, ...otherItemsList];
};

export const transformRenderingItems = (renderingDetails: RenderingDetails[]): RenderingDetails[] => {
    const startKey = "bill_heading";
    const endKey =
        renderingDetails.find((item) => item.key.toLowerCase() === RENDERING_DETAILS_KEYS.BILL_DETAILS_SECTION_END)
            ?.key ?? "grand_total";

    const startIndex = renderingDetails.findIndex((item) => item.key === startKey);
    const endIndex = renderingDetails.findIndex((item) => item.key === endKey);

    let removedItems: RenderingDetails[] = [];
    if (startIndex >= 0 && endIndex >= 0 && endIndex >= startIndex) {
        removedItems = renderingDetails.splice(startIndex + 1, endIndex - startIndex);
        renderingDetails[startIndex].children = [...removedItems];
    }

    // in Cart 2.0, checkout BE sends habit forming details in rendering details. So no need to append again at client
    return renderingDetails;
};

export function hideWidgetsBasedOnConfig(
    renderingDetails: RenderingDetails[],
    widgetInfo: ReviewedCartApiResponse["widget_info"],
) {
    if (isEmpty(widgetInfo)) {
        return renderingDetails;
    }
    const itemList = [];

    for (let index = 0; index < renderingDetails.length; index++) {
        const item = renderingDetails[index];
        const widgetConfig = widgetInfo?.[item.key];

        // push the item to the list
        if (widgetConfig == undefined || widgetConfig?.visible === true) {
            itemList.push(item);
        } else {
            // if item not visible then remove the transparent separator as well
            if (
                renderingDetails[index + 1]?.type.toUpperCase() === VALID_RENDERING_DETAILS_TYPE.TRANSPARENT_SEPARATOR
            ) {
                index += 1;
            }
        }
    }

    return itemList;
}

export function isFooterCroutonExists(cartResponse: ReviewedCart | null): boolean {
    return !isEmpty(cartResponse?.data?.crouton?.message);
}

export const filterRenderingDetails = (
    renderingDetails: RenderingDetails[],
    itemsTypes: string[],
): Omit<RenderingDetails, "children">[] => {
    return renderingDetails.filter((detailsList) => itemsTypes.includes(detailsList.type.toUpperCase()));
};

// This helper needs to be used after transforming rendering details from BE by using above helper `transformRenderingItems`
export const getBillDetails = (renderingDetails: RenderingDetails[]): RenderingDetails | undefined => {
    return renderingDetails.find(
        (detailsList) => detailsList.key.toLowerCase() === RENDERING_DETAILS_KEYS.BILL_HEADING,
    );
};
